import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileSearch, Download, Loader2, FileText, X } from "lucide-react";
import { api } from "../../lib/api";
import { handlePdfError } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { opCost, creditLabel, tierName } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import ToolIntro from "./ToolIntro";
import PreviewUnlock from "./PreviewUnlock";

const KIND = "searchable";

export default function SearchablePdfTab() {
  const [file, setFile] = useState(null);
  const [lang, setLang] = useState("eng");
  const [languages, setLanguages] = useState([{ code: "eng", name: "English" }]);
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const inputRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();

  const cost = estimate?.total_cost ?? opCost(user, KIND);

  useEffect(() => {
    api.get("/ocr/languages")
      .then((res) => { if (res.data?.languages?.length) setLanguages(res.data.languages); })
      .catch((e) => console.error("Could not load OCR languages:", e));
  }, []);

  useEffect(() => {
    if (!file) { setEstimate(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const form = new FormData();
        form.append("kind", KIND);
        form.append("file", file);
        const res = await api.post("/pdf/estimate", form, { headers: { "Content-Type": "multipart/form-data" } });
        if (!cancelled) setEstimate(res.data);
      } catch (e) { console.error("Cost estimate failed:", e); }
    })();
    return () => { cancelled = true; };
  }, [file]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Please upload a PDF file"); return; }
    setFile(f);
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files?.[0]); };

  const convert = async () => {
    if (!file) { toast.error("Upload a PDF first"); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("lang", lang);
      form.append("file", file);
      const res = await api.post("/pdf/searchable", form, { headers: { "Content-Type": "multipart/form-data" } });
      setStage(res.data);
    } catch (e) {
      if (handlePdfError(e) === "no-credits") navigate("/buy-credits");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <ToolIntro
        Icon={FileSearch}
        title="Make PDF Searchable"
        desc="Turn a scanned PDF into a searchable one. We keep the page exactly as it looks and add an invisible text layer, so you can select, copy and search the text. Large files add 1 credit per 30 pages over 30."
        accepts="PDF up to 25 MB"
        tier={tierName(KIND)}
        costLabel={`${creditLabel(cost)}${estimate?.pages ? ` · ${estimate.pages} pages` : ""}`}
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="searchable-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
          drag ? "border-indigo-500 bg-indigo-50" : "border-slate-300 bg-white hover:border-indigo-400 hover:bg-slate-50"
        }`}
      >
        <input ref={inputRef} data-testid="searchable-file-input" type="file" accept=".pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
        <div className="h-14 w-14 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <FileSearch className="h-7 w-7 text-indigo-600" />
        </div>
        <p className="font-display text-lg font-semibold">Drop a scanned PDF here or click to browse</p>
        <p className="text-slate-500 text-sm mt-1">We'll add a selectable, searchable text layer.</p>
      </div>

      {file && (
        <div className="mt-4 flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid="searchable-file-chip">
          <FileText className="h-5 w-5 text-red-500" />
          <span className="flex-1 text-sm truncate">{file.name}</span>
          <span className="text-xs text-slate-400 font-mono">{(file.size / 1024).toFixed(0)} KB</span>
          <button onClick={() => setFile(null)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
        <label htmlFor="searchable-lang" className="block font-semibold text-sm">Document language (OCR)</label>
        <p className="text-xs text-slate-500 mb-2">Pick the language your scan is written in for the most accurate text recognition.</p>
        <select
          id="searchable-lang"
          data-testid="searchable-ocr-language"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="w-full sm:w-64 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
        >
          {languages.map((l) => (
            <option key={l.code} value={l.code}>{l.name}</option>
          ))}
        </select>
      </div>

      <div className="mt-6 flex justify-center">
        <Button
          data-testid="searchable-convert-button"
          onClick={convert}
          disabled={loading || !file || !online}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-12"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Create preview
        </Button>
      </div>

      {stage && <PreviewUnlock stage={stage} onClose={() => setStage(null)} onUnlocked={() => { setStage(null); setFile(null); }} />}
    </div>
  );
}
